import { Fieldset, Radio } from "@digdir/designsystemet-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormWrapper } from "@/components";
import {
  BestillingFormErrorSummary,
  type BestillingPageKey,
  useBestillingContext,
} from "@/features/bestillinger";
import {
  BestillingNavigationActions,
  FormNavigationBlocker,
  useBestillingNavigation,
} from "@/features/navigation";
import { Constants } from "@/features/shared/constants";
import { isDirty } from "@/utils";
import {
  bestillerPrivatFormSchema,
  type bestillerPrivatFormValues,
  getDefaultBestillerPrivatFormValues,
  translationPrefixPrivatForm,
} from "./BestillerPrivatFormSchemas";
import { Tekstfelt } from "./Tekstfelt";

const currentPageKey: BestillingPageKey = "/bestilling/ny/bestiller";

export function BestillerPrivatForm() {
  const { navigateNext } = useBestillingNavigation(currentPageKey);
  const { readOnly, bestilling, setBestillingData } = useBestillingContext();
  const { t } = useTranslation();

  const form = useForm({
    resolver: zodResolver(bestillerPrivatFormSchema),
    values: getDefaultBestillerPrivatFormValues(bestilling.bestillerPrivat),
    disabled: readOnly,
    mode: "onSubmit",
  });

  const updateBestillingQuery = (data: bestillerPrivatFormValues) => {
    // navigation blocker liker promise
    return Promise.resolve(
      setBestillingData({
        ...bestilling,
        bestillerSelskap: null, // Hvis bruker har valgt selskap, fylt ut, gått tilbake og valgt privat, så nullstilles bestillerPrivat
        bestillerPrivat: {
          ...data,
          postNummer: Number(data.postnummer),
          sted: data.poststed,
        },
      }),
    );
  };

  async function handleSubmit(data: bestillerPrivatFormValues) {
    if (isDirty(form.formState)) {
      await updateBestillingQuery(data);
    }
    await navigateNext();
  }
  return (
    <section>
      <FormProvider {...form}>
        <FormNavigationBlocker form={form} onSubmit={updateBestillingQuery} />
        <FormWrapper onSubmit={form.handleSubmit(handleSubmit)}>
          <Fieldset>
            <Fieldset.Legend>
              {t(`${translationPrefixPrivatForm}.felt`, {
                context: "boligType",
              })}
            </Fieldset.Legend>
            <div className="flex flex-wrap gap-4">
              {Constants.PRIVAT_BOLIGTYPER.map((boligType) => (
                <Radio
                  key={boligType}
                  label={t("enums.boligType", { context: boligType })}
                  value={boligType}
                  {...form.register("boligType")}
                  name="boligType"
                />
              ))}
            </div>
          </Fieldset>
          <Tekstfelt
            feltKey="kundenummer"
            readOnly={readOnly}
            maxLength={256}
            hjelpeTekst={true}
            labelTranslationPrefix={translationPrefixPrivatForm}
          />
          <Tekstfelt
            feltKey="målerId"
            readOnly={readOnly}
            maxLength={256}
            hjelpeTekst={true}
            labelTranslationPrefix={translationPrefixPrivatForm}
          />
          <Tekstfelt
            feltKey="adresse"
            readOnly={readOnly}
            maxLength={256}
            labelTranslationPrefix={translationPrefixPrivatForm}
            autoComplete="street-address"
          />
          <section className="flex gap-4 flex-row flex-wrap sm:flex-nowrap">
            <Tekstfelt
              feltKey="postnummer"
              type="text"
              size={4}
              minLength={4}
              maxLength={4}
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="postal-code" // ZIP code
              readOnly={readOnly}
              labelTranslationPrefix={translationPrefixPrivatForm}
            />
            <Tekstfelt
              className="w-full"
              feltKey="poststed"
              maxLength={256}
              autoComplete="address-level2" // City
              readOnly={readOnly}
              labelTranslationPrefix={translationPrefixPrivatForm}
            />
          </section>

          <BestillingFormErrorSummary errors={form.formState.errors} />

          <BestillingNavigationActions
            className="pt-6"
            currentPageKey={currentPageKey}
            disableNext={readOnly}
          />
        </FormWrapper>
      </FormProvider>
    </section>
  );
}
